import type { User, UserWorkspace } from '@altsui/shared';

export const AUTH_STORAGE_KEY = 'auth-storage';

type PersistedAuthState = {
  user: User | null;
  activeFundId: string | null;
  workspaces: UserWorkspace[];
  isAuthenticated: boolean;
};

type PersistedAuthEnvelope = {
  state?: Partial<PersistedAuthState>;
  version?: number;
};

function readEnvelope(): PersistedAuthEnvelope | null {
  const raw = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as PersistedAuthEnvelope;
  } catch {
    return null;
  }
}

function getStoredAuthSession(): PersistedAuthState {
  const state = readEnvelope()?.state ?? {};
  return {
    user: state.user ?? null,
    activeFundId: state.activeFundId ?? state.user?.fundId ?? null,
    workspaces: state.workspaces ?? [],
    isAuthenticated: Boolean(state.isAuthenticated && state.user),
  };
}

/** Reads the user id out of a raw persisted envelope, e.g. a storage event value. */
export function readPersistedUserId(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const envelope = JSON.parse(raw) as PersistedAuthEnvelope;
    return envelope.state?.user?.id ?? null;
  } catch {
    return null;
  }
}

export function getStoredActiveFundId(): string | null {
  const session = getStoredAuthSession();
  return session.activeFundId ?? session.user?.fundId ?? null;
}

export function clearStoredAuthSession(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
}
